/**
 * Option catalogs for the Patient Eligibility Mapping worklist.
 *
 * Mirrors `GET /clinical-trials-service/api/v1/eligibility/options`.
 */

import type { FilterSection } from "./filterValues";

/**
 * Value/label pair used by eligibility dropdowns, filters, and tabs.
 */
export interface EligibilitySelectOption {
  value: string;
  label: string;
  /** Optional record total displayed beside a worklist tab. */
  count?: number | null;
}

/**
 * Facility trial option used by the worklist header.
 */
export interface EligibilityTrialOption extends EligibilitySelectOption {
  nct_id: string | null;
  sponsor: string | null;
  sponsor_protocol_id: string | null;
  /** Trial phase, such as `2/3`. */
  phase: string | null;
  /** Trial design, such as `Randomised`. */
  type: string | null;
  region: string | null;
  site_status: string | null;
}

/**
 * Full options payload that drives header, tabs, and worklist filters.
 */
export interface EligibilityOptionsResponse {
  trials: EligibilityTrialOption[];
  patient_status: EligibilitySelectOption[];
  eligibility_status: EligibilitySelectOption[];
  eligibility_tier: EligibilitySelectOption[];
  tabs: EligibilitySelectOption[];
}

/**
 * One labeled metadata chip rendered under the selected trial title.
 */
export interface EligibilityTrialDataPoint {
  label: string;
  value: string;
}

/** Placeholder used for unavailable trial header metadata. */
export const EMPTY_TRIAL_METADATA = "--";

/**
 * Resolve the worklist header title from a trial option.
 *
 * @input trial - Selected trial option from the options API
 * @return string: Protocol id when present, otherwise the option label or value
 */
export function getTrialTitle(trial: EligibilityTrialOption): string {
  return trial.sponsor_protocol_id || trial.label || trial.value;
}

/**
 * Build the trial metadata row shown under the protocol title.
 * Missing phase, type, or sponsor values stay in the row as placeholders.
 *
 * @input trial - Selected trial option from the options API
 * @return EligibilityTrialDataPoint[]: Phase, type, and sponsor
 */
export function getTrialDataPoints(
  trial: EligibilityTrialOption,
): EligibilityTrialDataPoint[] {
  return [
    { label: "Phase", value: trial.phase || EMPTY_TRIAL_METADATA },
    { label: "Type", value: trial.type || EMPTY_TRIAL_METADATA },
    { label: "Sponsor", value: trial.sponsor || EMPTY_TRIAL_METADATA },
  ];
}

/**
 * Show a trial metadata value, or the placeholder when it is blank.
 *
 * @input value - Raw field from the options API
 * @return string: Trimmed value, or {@link EMPTY_TRIAL_METADATA}
 */
function displayTrialMetadata(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed || EMPTY_TRIAL_METADATA;
}

/**
 * Map an options catalog into a value-to-label lookup.
 *
 * @input options - Value/label pairs from the options API
 * @return Record<string, string>: Label for each catalog value
 */
export function eligibilityOptionLabels(
  options: EligibilitySelectOption[],
): Record<string, string> {
  return Object.fromEntries(options.map((option) => [option.value, option.label]));
}

/**
 * Map options catalogs into RisaCustomTable filter sections.
 *
 * String catalogs are omitted when empty. Next-visit is always included as a
 * date-range filter because the screened-rows API accepts visit bounds.
 *
 * @input options - Eligibility options payload
 * @return FilterSection[]: Filter sections with at least one option
 */
export function buildEligibilityFilterSections(
  options: EligibilityOptionsResponse,
): FilterSection[] {
  const additionalStudyOptions = options.trials.map((trial) => ({
    value: trial.value,
    label: trial.label || trial.value,
  }));

  return [
    {
      id: "eligibility_tier",
      label: "Tier",
      type: "string" as const,
      options: options.eligibility_tier,
    },
    {
      id: "additional_study",
      label: "Additional Studies",
      type: "string" as const,
      options: additionalStudyOptions,
    },
    {
      id: "eligibility_status",
      label: "Eligibility Status",
      type: "string" as const,
      options: options.eligibility_status,
    },
    {
      id: "next_md_visit",
      label: "Next MD Visit",
      type: "date" as const,
      options: [],
    },
  ].filter(
    (section) => section.type === "date" || section.options.length > 0,
  );
}
